import { useState } from 'react'
import { serializeError } from '@w/utils'
import { useErrorBoundaryDomain } from '../hooks/use-error-boundary-domain'
import { orderedErrorEntries } from './error-details.order'
import { useTranslation } from '../i18n'

import type { ErrorDetailsProps } from './error-details.types'
export type { ErrorDetailsProps } from './error-details.types'

export function ErrorDetails({ error, title, description, compact, diagnosticsOnly }: ErrorDetailsProps) {
  const { t } = useTranslation('ui')
  const [expanded, setExpanded] = useState(false)
  const { error: normalized, domainText } = useErrorBoundaryDomain(error)
  const message = domainText ?? normalized?.message ?? ''
  return (
    <div className={`flex min-w-0 max-w-full flex-col text-sm ${compact ? 'gap-2' : 'gap-4'}`}>
      {title && <p className="font-medium">{title}</p>}
      {description && <p className="text-on-surface-secondary max-w-md">{description}</p>}
      {!diagnosticsOnly && <p className="text-error [overflow-wrap:anywhere]">{message.trim().length > 0 ? message : t('platform:error.generic.message')}</p>}
      <details className="min-w-0 max-w-full text-left" onToggle={(event) => { setExpanded(event.currentTarget.open) }}>
        <summary className="text-on-surface-secondary cursor-pointer rounded-sm py-3 select-none focus-visible:outline-2 focus-visible:outline-offset-4">{diagnosticsOnly ? t('platform:error.technicalDetails') : t('platform:error.details')}</summary>
        <div className="bg-surface-2 border-border mt-2 min-w-0 max-w-full rounded-xl border p-4">
          {expanded && <SerializedErrorTree value={serializeError(error)} />}
        </div>
      </details>
    </div>
  )
}

function SerializedErrorTree({ value }: { value: ReturnType<typeof serializeError> }) {
  return (
    <dl className="flex flex-col gap-3 text-xs">
      {orderedErrorEntries(value).map(([key, field]) => field === undefined ? null : (
        <div key={key} className="flex min-w-0 flex-col gap-1">
          <dt className="font-medium">{key}</dt>
          <dd className={key === 'cause' ? 'border-border min-w-0 border-l pl-3' : 'min-w-0'}>
            {key === 'cause' && value.cause ? <SerializedErrorTree value={value.cause} /> : (
              <pre className="font-mono leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{typeof field === 'string' && key !== 'details' ? field : JSON.stringify(field, null, 2)}</pre>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
